import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { Checkbox } from '../../components/input/checkbox.tsx'
import type {
  ThreatModelActor,
  ThreatModelContent,
  ThreatModelEntryPoint,
  ThreatModelSecurityControl,
  ThreatModelSensitiveAsset,
} from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as detailStyles from './graph-detail.css.ts'
import { GraphViewport } from './graph-viewport.tsx'
import * as styles from './threat-model-graph.css.ts'
import { type NodeTone } from './threat-model-graph.css.ts'

/**
 * The threat model as a trust-zone flow.
 *
 * Concentric bands, outermost untrusted: actors sit in the band their `trustLevel` puts them in, entry
 * points ride the outer edge of the DMZ because the payload says where input *crosses* but not which zone
 * each entry point belongs to, and sensitive assets sit in the core.
 *
 * **Plain SVG rather than a graph library.** Every position here is trigonometry against a radius; d3 would
 * contribute only `select`, which React already does, and would then fight React for ownership of these
 * nodes. If we later need force layout or real graph algorithms that changes, but not for concentric bands.
 *
 * **Nothing is inferred.** Actors are banded by a closed enum; entry points, assets and controls are placed
 * by their role in the chain, not by guessing a zone from free text. Dan's note suggests a heuristic over
 * `authentication` and `can_reach` could push entry points into zones — deliberately not done here, because
 * a diagram that silently mixes stated and guessed facts is worse than one that admits what it knows.
 */

/**
 * Label sizes in on-screen pixels. Every label divides by the viewport zoom so it renders at this size
 * whatever the scale — text that grows with the diagram overlaps just as badly at 400% as at 100%.
 */
const LABEL_PX = 11
const BAND_LABEL_PX = 11
const BOUNDARY_LABEL_PX = 11

const VIEWBOX = 760
const CENTRE = VIEWBOX / 2
const R_MAX = 320

type BandId = 'external' | 'dmz' | 'internal'

type Band = {
  id: BandId
  label: string
  /** Outer radius as a fraction of `R_MAX`. */
  outer: number
  inner: number
}

const FULL_BANDS: Band[] = [
  { id: 'external', label: 'External · untrusted', outer: 1, inner: 0.68 },
  { id: 'dmz', label: 'Semi-trusted · DMZ', outer: 0.68, inner: 0.4 },
  { id: 'internal', label: 'Trusted · internal', outer: 0.4, inner: 0 },
]

const COLLAPSED_BANDS: Band[] = [
  { id: 'external', label: 'Public · untrusted', outer: 1, inner: 0.52 },
  { id: 'internal', label: 'Internal · trusted', outer: 0.52, inner: 0 },
]

/**
 * `trust_level` is `z.string()` on the wire, not an enum — the generator invents values and the UI has to
 * degrade. An unrecognised level bands as `internal` and paints as `internal`: the *least* alarming
 * placement, so a value we do not understand never reads as a stronger claim than the model made.
 */
const TRUST_LEVELS = ['anonymous', 'authenticated', 'privileged', 'service', 'internal'] as const
type TrustLevel = (typeof TRUST_LEVELS)[number]
export const coerceTrustLevel = (value: string): TrustLevel =>
  TRUST_LEVELS.find(
    level =>
      level ===
      value
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
  ) ?? 'internal'

const bandForTrust = (rawTrustLevel: string, isCollapsed: boolean): BandId => {
  const trustLevel = coerceTrustLevel(rawTrustLevel)
  if (isCollapsed) {
    return trustLevel === 'anonymous' || trustLevel === 'authenticated' ? 'external' : 'internal'
  }
  if (trustLevel === 'anonymous') {
    return 'external'
  }
  if (trustLevel === 'authenticated') {
    return 'dmz'
  }
  return 'internal'
}

type Placed<T> = T & { x: number; y: number }

/**
 * Spreads items evenly around a ring.
 *
 * Exported for its test: the placement is the one piece of real logic here, and a diagram whose nodes
 * overlap or leave a gap is hard to catch by eye once there are eleven of them.
 */
export const placeOnRing = <T,>({
  items,
  radiusFraction,
  startDegrees = -90,
}: {
  items: T[]
  radiusFraction: number
  startDegrees?: number
}): Placed<T>[] => {
  const radius = radiusFraction * R_MAX
  return items.map((item, index) => {
    const angle = ((startDegrees + index * (360 / Math.max(items.length, 1))) * Math.PI) / 180
    return { ...item, x: Math.cos(angle) * radius, y: Math.sin(angle) * radius }
  })
}

type Selection =
  | { kind: 'actor'; value: ThreatModelActor }
  | { kind: 'entryPoint'; value: ThreatModelEntryPoint }
  | { kind: 'asset'; value: ThreatModelSensitiveAsset }
  | { kind: 'control'; value: ThreatModelSecurityControl }
  | { kind: 'boundary'; value: { name: string; description: string; crosses: string } }

export type ThreatModelGraphProps = {
  content: ThreatModelContent
}

type GraphOptionProps = {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => unknown
}

const GraphOption: React.FC<GraphOptionProps> = ({ id, label, checked, onCheckedChange }) => (
  <div className={styles.option}>
    <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} ariaLabel={label} />
    <label htmlFor={id} className={styles.optionLabel}>
      {label}
    </label>
  </div>
)

export const ThreatModelGraph: React.FC<ThreatModelGraphProps> = ({ content }) => {
  const model = content
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [showFlows, setShowFlows] = useState(true)
  const [selection, setSelection] = useState<Selection>()
  // Hover previews, click pins — matching the coverage graph. `hovered` wins so a pointer can browse the
  // model without losing the pinned node, which returns the moment the pointer leaves.
  const [hovered, setHovered] = useState<Selection>()
  const shown = hovered ?? selection

  const bands = isCollapsed ? COLLAPSED_BANDS : FULL_BANDS

  const placed = useMemo(() => {
    const byBand = (id: BandId) => model.actors.filter(actor => bandForTrust(actor.trust_level, isCollapsed) === id)
    const ringFor = (id: BandId) => {
      const band = bands.find(candidate => candidate.id === id)
      return band ? (band.outer + band.inner) / 2 : 0.5
    }

    return {
      actors: bands.flatMap(band =>
        placeOnRing({ items: byBand(band.id), radiusFraction: ringFor(band.id), startDegrees: -90 })
      ),
      // Entry points ride the DMZ's outer edge: the payload states that input crosses here, not which zone
      // any single route belongs to.
      entry_points: placeOnRing({
        items: model.entry_points,
        radiusFraction: isCollapsed ? 0.52 : 0.68,
        startDegrees: -84,
      }),
      assets: placeOnRing({ items: model.sensitive_assets, radiusFraction: 0.18, startDegrees: -90 }),
      controls: placeOnRing({
        items: model.security_controls,
        radiusFraction: isCollapsed ? 0.34 : 0.4,
        startDegrees: -78,
      }),
    }
  }, [model, bands, isCollapsed])

  // One seam per join between two bands, in payload order.
  const drawnBoundaries = model.trust_boundaries.slice(0, bands.length - 1)

  const selectedName = selection && 'name' in selection.value ? selection.value.name : undefined
  const isDimmed = (name: string) => Boolean(selectedName) && selectedName !== name

  return (
    <div className={styles.graph}>
      <div className={styles.canvasPanel}>
        {/* Checkboxes, not chips: these three are independent on/off switches, and a pressed-button chip
            reads as a filter that might be exclusive. */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarOptions}>
            <GraphOption
              id="graph-collapse-zones"
              label="Collapse zones"
              checked={isCollapsed}
              onCheckedChange={setIsCollapsed}
            />
            <GraphOption
              id="graph-show-controls"
              label={`Controls (${model.security_controls.length})`}
              checked={showControls}
              onCheckedChange={setShowControls}
            />
            <GraphOption id="graph-show-flows" label="Flows" checked={showFlows} onCheckedChange={setShowFlows} />
          </div>
          <GraphLegend />
        </div>

        {/* A seam is the join between two zones, so the canvas has exactly one fewer seam than it has
            bands — two or three, whatever the model listed. Saying which ones are drawn beats dropping
            the rest in silence, and the Lists view carries every one with its description. */}
        {model.trust_boundaries.length > drawnBoundaries.length && (
          <p className={styles.canvasNote}>
            {`Drawing ${drawnBoundaries.length} of ${model.trust_boundaries.length} trust ${pluralize(
              model.trust_boundaries.length,
              'boundary',
              'boundaries'
            )} as zone seams — every one is listed under Lists.`}
          </p>
        )}

        <GraphViewport
          contentSize={{ width: VIEWBOX, height: VIEWBOX }}
          ariaLabel="Trust zone flow"
          onBackgroundClick={() => setSelection(undefined)}
        >
          {scale => (
            /* `group`, not `img`: `role="img"` makes the whole subtree presentational, which removed all 28
               `role="button"` nodes from the accessibility tree while leaving them focusable — so a keyboard
               user could tab onto a node that announced nothing. The coverage graph already does this. */
            <svg
              width={VIEWBOX * scale}
              height={VIEWBOX * scale}
              viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
              role="group"
              aria-label="Trust zone flow"
            >
              <g transform={`translate(${CENTRE} ${CENTRE})`}>
                {/* Outermost first so inner bands paint over it — concentric fills without masks. */}
                {bands.map(band => (
                  <circle
                    key={band.id}
                    className={styles.bandFill({ zone: band.id })}
                    r={band.outer * R_MAX}
                    fillOpacity={0.14}
                    strokeOpacity={0.4}
                    strokeWidth={1}
                  />
                ))}

                {/* Zone labels hug the left of each ring, clear of the nodes, which start at the top. They
                    counter-scale like the node labels: left to grow with the diagram they swallowed it. */}
                {bands.map(band => (
                  <text
                    key={`${band.id}-label`}
                    className={styles.bandLabel}
                    x={-(band.outer * R_MAX) + 14 / scale}
                    y={4 / scale}
                    textAnchor="start"
                    style={{ fontSize: `${BAND_LABEL_PX / scale}px` }}
                  >
                    {band.label}
                  </text>
                ))}

                {drawnBoundaries.map((boundary, index) => {
                  const seam = (bands[index]?.inner ?? 0.5) * R_MAX
                  return (
                    <g
                      key={boundary.name}
                      onClick={event => {
                        event.stopPropagation()
                        setSelection({ kind: 'boundary', value: boundary })
                      }}
                      onMouseEnter={() => setHovered({ kind: 'boundary', value: boundary })}
                      onMouseLeave={() => setHovered(undefined)}
                    >
                      <circle className={styles.boundaryLine} r={seam} fill="none" strokeWidth={1.5} />
                      <text
                        className={styles.boundaryLabel}
                        x={seam - 10 / scale}
                        y={-6 / scale}
                        textAnchor="end"
                        style={{ fontSize: `${BOUNDARY_LABEL_PX / scale}px` }}
                      >
                        {boundary.name}
                      </text>
                    </g>
                  )
                })}

                {showFlows &&
                  placed.entry_points.map(entryPoint => (
                    <line
                      key={`flow-${entryPoint.name}-${entryPoint.location}`}
                      className={styles.flowEdge}
                      x1={entryPoint.x}
                      y1={entryPoint.y}
                      x2={0}
                      y2={0}
                      strokeOpacity={0.6}
                      strokeWidth={1.5}
                    />
                  ))}

                {placed.assets.map((asset, index) => (
                  <GraphNode
                    key={`asset-${asset.name}-${asset.location}`}
                    x={asset.x}
                    y={asset.y}
                    label={asset.name}
                    shapeClassName={styles.assetFill}
                    isDimmed={isDimmed(asset.name)}
                    isSelected={selectedName === asset.name}
                    labelScale={1 / scale}
                    onSelect={() => setSelection({ kind: 'asset', value: asset })}
                    onHover={() => setHovered({ kind: 'asset', value: asset })}
                    onHoverEnd={() => setHovered(undefined)}
                    labelBelow={index % 2 === 0}
                  />
                ))}

                {showControls &&
                  placed.controls.map((control, index) => (
                    <GraphNode
                      key={`control-${control.name}-${control.location}`}
                      x={control.x}
                      y={control.y}
                      label={control.name}
                      shapeClassName={styles.controlFill}
                      radius={6}
                      isDimmed={isDimmed(control.name)}
                      isSelected={selectedName === control.name}
                      labelScale={1 / scale}
                      onSelect={() => setSelection({ kind: 'control', value: control })}
                      onHover={() => setHovered({ kind: 'control', value: control })}
                      onHoverEnd={() => setHovered(undefined)}
                      labelBelow={index % 2 === 0}
                    />
                  ))}

                {placed.entry_points.map((entryPoint, index) => (
                  <GraphNode
                    key={`entry-${entryPoint.name}-${entryPoint.location}`}
                    x={entryPoint.x}
                    y={entryPoint.y}
                    label={entryPoint.name}
                    shapeClassName={styles.entryPointFill}
                    isDimmed={isDimmed(entryPoint.name)}
                    isSelected={selectedName === entryPoint.name}
                    labelScale={1 / scale}
                    onSelect={() => setSelection({ kind: 'entryPoint', value: entryPoint })}
                    onHover={() => setHovered({ kind: 'entryPoint', value: entryPoint })}
                    onHoverEnd={() => setHovered(undefined)}
                    labelBelow={index % 2 === 0}
                  />
                ))}

                {placed.actors.map((actor, index) => (
                  <GraphNode
                    key={`actor-${actor.name}`}
                    x={actor.x}
                    y={actor.y}
                    label={actor.name}
                    shapeClassName={styles.actorFill({ level: coerceTrustLevel(actor.trust_level) })}
                    radius={10}
                    isDimmed={isDimmed(actor.name)}
                    isSelected={selectedName === actor.name}
                    labelScale={1 / scale}
                    onSelect={() => setSelection({ kind: 'actor', value: actor })}
                    onHover={() => setHovered({ kind: 'actor', value: actor })}
                    onHoverEnd={() => setHovered(undefined)}
                    labelBelow={index % 2 === 0}
                  />
                ))}
              </g>
            </svg>
          )}
        </GraphViewport>
      </div>

      <GraphDetailPanel selection={shown} />
    </div>
  )
}

const LEGEND: { tone: NodeTone; label: string }[] = [
  { tone: 'anonymous', label: 'Anonymous' },
  { tone: 'authenticated', label: 'Authenticated' },
  { tone: 'privileged', label: 'Privileged' },
  { tone: 'service', label: 'Service' },
  { tone: 'internal', label: 'Internal' },
  { tone: 'entryPoint', label: 'Entry point' },
  { tone: 'asset', label: 'Asset' },
  { tone: 'control', label: 'Control' },
]

/** The key for every dot on the canvas, not just the actor ramp — the other three were unexplained. */
const GraphLegend: React.FC = () => (
  <ul className={styles.legend} aria-label="Node colour key">
    {LEGEND.map(entry => (
      <li key={entry.tone} className={styles.legendItem}>
        <span className={styles.legendSwatch({ tone: entry.tone })} aria-hidden />
        {entry.label}
      </li>
    ))}
  </ul>
)

type GraphNodeProps = {
  x: number
  y: number
  label: string
  shapeClassName: string
  radius?: number
  isDimmed: boolean
  isSelected: boolean
  onSelect: () => void
  onHover: () => void
  onHoverEnd: () => void
  /**
   * Reciprocal of the viewport zoom. Labels are sized in SVG units, which the viewport's CSS transform
   * then scales — so a label drawn at a fixed size grows with the diagram and overlaps its neighbours just
   * as badly at 400% as at 100%. Dividing by the zoom holds every label at a constant on-screen size, so
   * zooming in genuinely separates them.
   */
  labelScale: number
  /** Alternated by the caller so neighbours on the same ring do not overwrite each other. */
  labelBelow?: boolean
}

const GraphNode: React.FC<GraphNodeProps> = ({
  x,
  y,
  label,
  shapeClassName,
  radius = 8,
  isDimmed,
  isSelected,
  onSelect,
  onHover,
  onHoverEnd,
  labelScale,
  labelBelow = true,
}) => {
  // The selected node's label gets a size bump and the full string: it is the one a reader is reading.
  const fontSize = LABEL_PX * labelScale * (isSelected ? 1.25 : 1)
  const gap = 12 * labelScale
  return (
    <g
      className={classNames(styles.nodeGroup, { [styles.dimmed]: isDimmed })}
      transform={`translate(${x} ${y})`}
      onClick={event => {
        // Without this the background rect below would clear the selection this click just made.
        event.stopPropagation()
        onSelect()
      }}
      role="button"
      aria-label={label}
      tabIndex={0}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      onFocus={onHover}
      onBlur={onHoverEnd}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect()
        }
      }}
    >
      <circle r={radius} className={shapeClassName} />
      <text
        className={classNames(styles.nodeLabel, { [styles.nodeLabelSelected]: isSelected })}
        x={0}
        y={labelBelow ? radius + gap : -(radius + gap * 0.6)}
        textAnchor="middle"
        style={{ fontSize: `${fontSize}px`, strokeWidth: `${1.5 * labelScale}px` }}
      >
        {isSelected || label.length <= 24 ? label : `${label.slice(0, 23)}…`}
      </text>
    </g>
  )
}

const SELECTION_LABELS: Record<Selection['kind'], string> = {
  actor: 'Actor',
  entryPoint: 'Entry point',
  asset: 'Sensitive asset',
  control: 'Security control',
  boundary: 'Trust boundary',
}

/**
 * The same pane the coverage graph uses, on the trust-zone graph's data.
 *
 * Both answer "what does the model say about the thing I just selected?", so they share
 * `graph-detail.css.ts` rather than two lookalike stylesheets — see that file. The one addition here is
 * a tag list: `mitigates` is a list on the payload, and joining it into a sentence lost that.
 */
const GraphDetailPanel: React.FC<{ selection?: Selection }> = ({ selection }) => {
  if (!selection) {
    return (
      <aside className={detailStyles.detail} aria-label="Graph selection">
        <div className={detailStyles.eyebrow}>Detail</div>
        <p className={detailStyles.detailEmpty}>
          Select a node to see what the model says about it. Every field is read straight from the payload, so nothing
          here is inferred.
        </p>
      </aside>
    )
  }

  const fields = ((): { label: string; text?: string; tags?: string[] }[] => {
    switch (selection.kind) {
      case 'actor':
        return [
          { label: 'Authenticates via', text: selection.value.authenticates_via },
          { label: 'Can reach', text: selection.value.can_reach },
        ]
      case 'entryPoint':
        return [
          { label: 'Authentication', text: selection.value.authentication },
          { label: 'Description', text: selection.value.description },
        ]
      case 'asset':
        return [{ label: 'Why it matters', text: selection.value.description }]
      case 'control':
        return [
          { label: 'Scope', text: selection.value.scope },
          { label: 'Declares it mitigates', tags: selection.value.mitigates },
        ]
      case 'boundary':
        return [
          { label: 'What separates the sides', text: selection.value.description },
          { label: 'Crosses', text: selection.value.crosses },
        ]
    }
  })()

  // The badge carries the one categorical fact each kind has, which is `kind` for most and `trust_level`
  // for an actor. A boundary has neither, so it gets no badge rather than an empty one.
  // The badge tone is the *dot's* tone, which for an actor is its trust level rather than "actor" — that
  // mismatch is what made a red anonymous dot sit beside a yellow badge.
  const tone: NodeTone = selection.kind === 'actor' ? coerceTrustLevel(selection.value.trust_level) : selection.kind
  const badge =
    selection.kind === 'actor'
      ? selection.value.trust_level
      : 'kind' in selection.value
        ? selection.value.kind.replace(/_/g, ' ')
        : undefined
  const location = 'location' in selection.value ? selection.value.location : undefined

  return (
    <aside className={detailStyles.detail} aria-label="Graph selection">
      <div className={detailStyles.eyebrow}>{SELECTION_LABELS[selection.kind]}</div>
      <h4 className={detailStyles.detailTitle}>{selection.value.name}</h4>
      {badge && <span className={classNames(detailStyles.badgeBase, styles.detailBadge({ tone }))}>{badge}</span>}
      {fields.map(field => (
        <div key={field.label} className={detailStyles.field}>
          <span className={detailStyles.fieldKey}>{field.label}</span>
          {field.tags ? (
            field.tags.length > 0 ? (
              <div className={detailStyles.tagList}>
                {field.tags.map(tag => (
                  <span key={tag} className={detailStyles.tag}>
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className={detailStyles.fieldValue}>— (nothing recorded)</span>
            )
          ) : (
            <span className={detailStyles.fieldValue}>{field.text || '—'}</span>
          )}
        </div>
      ))}
      {location && (
        <div className={detailStyles.field}>
          <span className={detailStyles.fieldKey}>Location</span>
          <span className={detailStyles.fieldLocation}>{location}</span>
        </div>
      )}
    </aside>
  )
}
